import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Building2 } from 'lucide-react';
import { api } from '@/api/api';
import { API_BASE } from '@/lib/api-client';
import { meKeys } from '@/features/auth/api';
import { getAuthMode, getDevLoginConfig } from '@/features/auth/mode';
import { AuroraBackground } from '@/components/shared/AuroraBackground';
import { Button } from '@/components/ui/button';

const signInSearchSchema = z.object({
  redirect: z.string().optional(),
});

export const Route = createFileRoute('/sign-in')({
  validateSearch: signInSearchSchema,
  component: SignInPage,
});

function SignInPage() {
  const { redirect } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mode = getAuthMode();
  const [formError, setFormError] = useState<string | null>(null);

  const finishLogin = async () => {
    await queryClient.invalidateQueries({ queryKey: meKeys.all });
    void navigate({ to: redirect ?? '/projects' });
  };

  const devLogin = useMutation({
    mutationFn: () => {
      const config = getDevLoginConfig();
      return api.devLogin({ email: config.email, display_name: config.displayName });
    },
    onSuccess: finishLogin,
    onError: () =>
      setFormError('Dev login failed. Is /api/dashboard/auth/dev-login mounted on the backend?'),
  });

  // Full-page navigation to the backend's OIDC kickoff — it redirects to
  // Keycloak, which redirects back through /auth/callback to set session cookies.
  const startSso = () => {
    window.location.assign(`${API_BASE}/auth/login`);
  };

  return (
    <>
      <AuroraBackground />
      <div className="relative z-10 flex min-h-screen items-center justify-center p-8 text-text">
        <div className="w-full max-w-sm space-y-6 rounded-lg border border-border bg-bg p-8 shadow-xl">
          <div className="space-y-1 text-center">
            <h1 className="text-2xl font-medium text-primary">Sign in</h1>
            <p className="text-sm text-text-muted">PM Dashboard — Accenture internal</p>
          </div>

          <div className="space-y-3">
            {mode === 'dev-login' ? (
              <Button
                type="button"
                variant="primary"
                className="w-full"
                onClick={() => {
                  setFormError(null);
                  devLogin.mutate();
                }}
                disabled={devLogin.isPending}
              >
                <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
                {devLogin.isPending ? 'Signing in…' : 'Continue with dev login'}
              </Button>
            ) : (
              <Button type="button" variant="primary" className="w-full" onClick={startSso}>
                <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
                Sign in with Keycloak
              </Button>
            )}

            {formError ? (
              <p
                role="alert"
                className="rounded-md border border-load-over-bg bg-load-over-bg/40 px-3 py-2 text-xs text-load-over-fg"
              >
                {formError}
              </p>
            ) : null}

            <p className="pt-1 text-center text-xs text-text-faint">
              Internal access only. Ask your project&apos;s PM to add you.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
